<template>
  <div class="dashboard-wrapper">
    <Sidebar
      :collapsed="sidebarCollapsed"
      :is-tablet="isTablet"
      :mobile-drawer-open="showSidebarMobile"
      :menu-items="module2MenuItems"
      :home-route="homeRoute"
      @update:collapsed="sidebarCollapsed = $event"
      @close-drawer="showSidebarMobile = false"
    />
    <button
      v-if="showFab"
      type="button"
      class="dashboard-fab"
      aria-label="Ouvrir le menu"
      @click="openSidebar"
    >
      <Bars3Icon class="w-6 h-6 text-white" />
    </button>
    <div
      class="dashboard-main"
      :class="{
        'dashboard-main--collapsed': sidebarCollapsed && !isTablet,
        'dashboard-main--tablet': isTablet,
      }"
      @click="onMainClick"
    >
      <Topbar @toggle-sidebar="showSidebarMobile = !showSidebarMobile" />
      <main class="dashboard-content m2-da app-premium-shell staff-shell">
        <router-view />
      </main>
      <EaseOfUseSurvey context="actions-mandat" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Bars3Icon } from '@heroicons/vue/24/outline';
import Sidebar from '@/components/dashboard/Sidebar.vue';
import Topbar from '@/components/dashboard/Topbar.vue';
import EaseOfUseSurvey from '@/components/dashboard/EaseOfUseSurvey.vue';
import { MODULE2_SIDEBAR_MENU_ITEMS } from '@/components/dashboard/sidebarProfileMenus';
import { getUserRole, isClientFacingRole } from '@/utils/auth';

export default {
  name: 'DashboardLayout',
  components: {
    Sidebar,
    Topbar,
    Bars3Icon,
    EaseOfUseSurvey,
  },
  setup() {
    const sidebarCollapsed = ref(false);
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const showSidebarMobile = ref(false);

    const isTablet = computed(() => windowWidth.value >= 777 && windowWidth.value < 1024);
    const isMobile = computed(() => windowWidth.value <= 776);
    const showFab = computed(() => isMobile.value && !showSidebarMobile.value);
    const homeRoute = computed(() =>
      isClientFacingRole(getUserRole())
        ? '/actions-mandat/mes-recommandations'
        : '/actions-mandat/dashboards'
    );

    function updateWidth() {
      windowWidth.value = window.innerWidth;
    }
    function openSidebar() {
      showSidebarMobile.value = true;
    }
    function closeSidebar() {
      showSidebarMobile.value = false;
    }

    onMounted(() => {
      updateWidth();
      window.addEventListener('resize', updateWidth);
    });
    onUnmounted(() => {
      window.removeEventListener('resize', updateWidth);
    });

    return {
      sidebarCollapsed,
      isTablet,
      showFab,
      openSidebar,
      showSidebarMobile,
      closeSidebar,
      module2MenuItems: MODULE2_SIDEBAR_MENU_ITEMS,
      homeRoute,
    };
  },
  methods: {
    onMainClick(ev) {
      if (!this.showSidebarMobile) return;
      if (ev.target && (ev.target.closest('.dashboard-topbar__menu-btn') || ev.target.closest('.dashboard-fab'))) return;
      this.closeSidebar();
    },
  },
};
</script>

<style scoped>
.dashboard-wrapper {
  @apply min-h-screen bg-dashboard-bg min-w-safe;
  font-family: var(--font-body);
  position: relative;
}

.dashboard-content :deep(p),
.dashboard-content :deep(.lead),
.dashboard-content :deep(.subtitle),
.dashboard-content :deep(.intro),
.dashboard-content :deep([class*="__lead"]),
.dashboard-content :deep([class*="__intro"]),
.dashboard-content :deep([class*="__description"]),
.dashboard-content :deep([class*="__msg"]),
.dashboard-content :deep(.exec-message),
.dashboard-content :deep(.reco-paragraph) {
  font-family: var(--font-body);
}

.dashboard-main {
  @apply min-h-screen transition-all duration-300 ease-out;
  margin-left: 0;
}

@media (min-width: 640px) {
  .dashboard-main {
    margin-left: 72px;
  }
}

@media (min-width: 1024px) {
  .dashboard-main {
    margin-left: 260px;
  }
  .dashboard-main--collapsed {
    margin-left: 72px;
  }
}

.dashboard-main--tablet {
  /* tablet margin set by media query above */
}

.dashboard-content {
  @apply p-3 sm:p-4 lg:p-6 xl:p-8;
  min-height: calc(100vh - 4rem);
}

.dashboard-fab {
  @apply fixed bottom-6 right-6 z-[950] flex items-center justify-center w-14 h-14 rounded-full shadow-lg lg:hidden;
  background: linear-gradient(135deg, var(--bank-gold) 0%, var(--bank-gold-dark) 100%);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.dashboard-fab:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 24px rgba(212, 175, 55, 0.35);
}
.dashboard-fab:active {
  transform: scale(0.98);
}
</style>
